import React from "react";
import { Box, Typography } from "@mui/material";
import { QUIZ } from "@/theme/quizTokens";
import { quizAsset } from "@/components/quiz/shared/quizAssets";

// Theme asset filename for the "three dogs eating" photo. Follows the same
// `pop-` naming convention as the other theme assets referenced via
// `| asset_url` in pop-testimonials.liquid (pop-trustpilot-stacked.png,
// pop-deco-review-heart.png, etc). Upload the real photo to the theme's
// /assets folder under this exact name so quizAsset() resolves it to the
// live CDN URL — same mechanism as `icon-sparkle` above in this file.
const RISK_FREE_PHOTO_ASSET = "pop-risk-free-bowls.jpg";

// Trustpilot's brand green isn't part of the QUIZ palette (it's tied to the
// review platform's own branding, not our theme), so it's kept as a local
// constant rather than a token — same treatment the site's other Trustpilot
// star rows use.
const TRUSTPILOT_GREEN = "#00b67a";

type Testimonial = {
    headline: string;
    quote: string;
    attribution: string;
};

type Props = {
    /** Dog's name, used in the headline + body copy. Falls back to "your dog". */
    dogName?: string;
    /** Possessive pronoun for the body copy ("her" / "his" / "their"). Defaults to "their" when unknown. */
    pronoun?: "her" | "his" | "their";
    /**
     * Bowl photo. Defaults to the theme asset above — override only if this
     * block ever needs a different photo than the standard "three dogs
     * eating" evidence shot.
     */
    imageSrc?: string;
    imageAlt?: string;
    /** Refund-guarantee body copy inside the green banner. */
    refundCopy?: string;
    /** Transition-plan reassurance line under the banner. */
    transitionCopy?: string;
    /** Trustpilot-style review card. Pass `null` to hide it entirely. */
    testimonial?: Testimonial | null;
};

const DEFAULT_TESTIMONIAL: Testimonial = {
    headline: "Even my fussy dog loves it",
    quote:
        "My extremely fussy cockapoo absolutely loves his Paws on Plates meals, and he always cleans his bowl!",
    attribution: "SG · Verified Trustpilot review",
};

/**
 * "RISK-FREE / Worried [Dog] Won't Touch It?" trust block.
 * Sizes per spec: eyebrow 13px mobile / 18px desktop, headline 30px,
 * body copy 13.5px, image caption 11px.
 *
 * Layout: full-bleed cream background band (spans the whole viewport width,
 * matching the "NO LOCK-IN" section) with a 601px content column centered
 * inside it. Previously the background color was applied directly to the
 * 601px-capped box, so the cream band only ever stretched as wide as the
 * content instead of going edge-to-edge — that's the width bug this fixes.
 */
export default function RiskFreeBlock({
    dogName,
    pronoun = "their",
    imageSrc,
    imageAlt,
    refundCopy = "You don't need to send anything back.",
    transitionCopy = "Every first box also comes with a free transition plan, because switching food too fast is usually what upsets a sensitive tummy.",
    testimonial = DEFAULT_TESTIMONIAL,
}: Props) {
    const name = dogName?.trim() || "your dog";

    return (
        // True full-bleed: 100vw + centering trick breaks this out of
        // whatever max-width container the page wraps ResultsStepPlan in.
        // A plain `width: 100%` (the previous attempt) only fills the
        // parent's width, not the actual browser viewport — that's why the
        // band was wider than the brown card but still short of the edges.
        <Box
            sx={{
                width: "100vw",
                position: "relative",
                left: "50%",
                right: "50%",
                marginLeft: "-50vw",
                marginRight: "-50vw",
                background: QUIZ.color.cream2,
                py: { xs: "28px", md: "40px" },
                boxSizing: "border-box",
            }}
        >
            <Box
                sx={{
                    maxWidth: { xs: 601, md: 720 },
                    mx: "auto",
                    width: "100%",
                    px: { xs: "16px", md: 0 },
                    boxSizing: "border-box",
                }}
            >
                <Box sx={{ maxWidth: 601, mx: "auto" }}>
                    {/* Eyebrow */}
                    <Typography
                        sx={{
                            fontFamily: QUIZ.font.body,
                            fontWeight: 800,
                            letterSpacing: "0.08em",
                            textAlign: "center",
                            color: QUIZ.color.ink,
                            fontSize: { xs: "13px", md: "18px" },
                            mb: { xs: "8px", md: "10px" },
                        }}
                    >
                        RISK-FREE
                    </Typography>

                    {/* Headline */}
                    <Typography
                        component="h2"
                        sx={{
                            fontFamily: QUIZ.font.display,
                            fontWeight: 900,
                            textTransform: "uppercase",
                            textAlign: "center",
                            color: QUIZ.color.ink,
                            fontSize: "30px",
                            lineHeight: 1.15,
                            mb: { xs: "12px", md: "14px" },
                        }}
                    >
                        Worried {name} Won&apos;t Touch It?
                    </Typography>

                    {/* Body copy */}
                    <Typography
                        sx={{
                            fontFamily: QUIZ.font.body,
                            textAlign: "center",
                            color: QUIZ.color.ink,
                            opacity: 0.9,
                            fontSize: "13.5px",
                            lineHeight: 1.4,
                            maxWidth: 420,
                            mx: "auto",
                            mb: { xs: "20px", md: "24px" },
                        }}
                    >
                        Most owners who tell us {name === "your dog" ? "their dog is" : `${name} is`} fussy are
                        surprised. But if {name === "your dog" ? "your dog turns" : `${name} turns`} {pronoun} nose
                        up, you&apos;re covered.
                    </Typography>

                    {/* Evidence photo */}
                    <Box
                        component="img"
                        src={quizAsset("risk-free-dogs-eating")}
                        alt={imageAlt || "Three dogs eating happily from their bowls"}
                        sx={{
                            width: "100%",
                            height: { xs: "auto", md: 540 },
                            objectFit: "cover",
                            objectPosition: "center bottom",
                            display: "block",
                            borderRadius: "14px",
                            mb: { xs: "10px", md: "12px" },
                        }}
                    />

                    {/* Caption */}
                    <Typography
                        sx={{
                            fontFamily: QUIZ.font.body,
                            textAlign: "center",
                            color: QUIZ.color.ink,
                            opacity: 0.65,
                            fontSize: { xs: "11px", md: "14px" },
                            lineHeight: 1.4,
                            maxWidth: 340,
                            mx: "auto",
                            mb: { xs: "20px", md: "24px" },
                        }}
                    >
                        The objection is &ldquo;will my dog eat it&rdquo;. This is the evidence, not decoration.
                    </Typography>
                </Box>

                {/* Refund guarantee banner */}
                <Box
                    sx={{
                        bgcolor: QUIZ.color.green,
                        borderRadius: "20px",
                        p: { xs: "20px 20px", md: "26px 28px" },
                        mb: { xs: "14px", md: "16px" },
                    }}
                >
                    <Typography
                        component="h3"
                        sx={{
                            fontFamily: QUIZ.font.display,
                            fontWeight: 900,
                            textTransform: "uppercase",
                            color: QUIZ.color.ink,
                            fontSize: { xs: "16px", md: "19px" },
                            lineHeight: 1.25,
                            mb: "8px",
                        }}
                    >
                        Email us within 30 days and we&apos;ll refund you in full
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: QUIZ.font.body,
                            color: QUIZ.color.ink,
                            opacity: 0.85,
                            fontSize: "14px",
                            lineHeight: 1.4,
                        }}
                    >
                        {refundCopy}
                    </Typography>
                </Box>

                {/* Transition-plan reassurance line */}
                <Typography
                    sx={{
                        fontFamily: QUIZ.font.body,
                        textAlign: "center",
                        color: QUIZ.color.ink,
                        opacity: 0.8,
                        fontSize: "13.5px",
                        lineHeight: 1.4,
                        mb: { xs: "14px", md: "16px" },
                    }}
                >
                    {transitionCopy}
                </Typography>

                {/* Trustpilot-style testimonial card */}
                {testimonial && (
                    <Box
                        sx={{
                            bgcolor: QUIZ.color.dark,
                            borderRadius: "20px",
                            p: { xs: "20px 20px", md: "26px 28px" },
                        }}
                    >
                        {/* Stars */}
                        <Box sx={{ display: "flex", gap: "3px", mb: "10px" }}>
                            {Array.from({ length: 5 }).map((_, i) => (
                                <Box
                                    key={i}
                                    sx={{
                                        width: { xs: 20, md: 22 },
                                        height: { xs: 20, md: 22 },
                                        bgcolor: TRUSTPILOT_GREEN,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: QUIZ.color.white,
                                        fontSize: "13px",
                                        lineHeight: 1,
                                    }}
                                >
                                    ★
                                </Box>
                            ))}
                        </Box>

                        <Typography
                            component="h3"
                            sx={{
                                fontFamily: QUIZ.font.display,
                                fontWeight: 900,
                                textTransform: "uppercase",
                                color: QUIZ.color.white,
                                fontSize: { xs: "14px", md: "17px" },
                                lineHeight: 1.3,
                                mb: "8px",
                            }}
                        >
                            {testimonial.headline}
                        </Typography>

                        <Typography
                            sx={{
                                fontFamily: QUIZ.font.body,
                                color: QUIZ.color.white,
                                opacity: 0.92,
                                fontSize: "14px",
                                lineHeight: 1.45,
                                mb: "10px",
                            }}
                        >
                            &ldquo;{testimonial.quote}&rdquo;
                        </Typography>

                        <Typography
                            sx={{
                                fontFamily: QUIZ.font.body,
                                color: QUIZ.color.white,
                                opacity: 0.55,
                                fontSize: "11.5px",
                            }}
                        >
                            {testimonial.attribution}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
}
